// Web 主应用入口

import "./antd.less";
import "./index.less";

import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useGlobalStore, useSkuPropertiesFilterStore } from "./store/hooks";
import { isFromAdmin } from "./store/util";
import StorageManager from "@/utils/StorageManager";
import { BrowserRouter as Router } from "react-router-dom";
import { ConfigProvider } from "antd";
import TopNavLayout from "./components/Layout/TopNavTheme/TopNavLayout";
import { showIsInDevDialog } from "./utils/dialog";
import ModalStoreProvider from "./hooks/Modal/ModalStoreProvider";
import ShopType from "./enum/ShopType";
import { ByteSecurityOpenInit } from "./services/doudianSecurity";
import { useModalStore } from "./hooks/Modal/hooks";
import PremiumUpgradeModal, { PremiumUpgradeModalProps } from "./components/PremiumUpgradeModal";
import AuthTokenHolder from "./services/http/AuthTokenHolder";
import { initDDJsLog } from "./services/doudianJSlog";
import GlobalShopExpiredModal from "./components/GlobalShopExpiredModal";
import MainRoute from "@/route";
import { getIsNeedLoadData } from "@/route/config";

import "@/services/http/axiosInjectInterceptor";
import { isEcMarketShopExpired, SupplierShopTypes } from "./utils/ShopUtils";
import * as dataTrack from "./services/dataTrack";
import { showChannelShopApproveEwaybillAuthTips } from "./services/channelShopAppove";
import { JdSecurityInit } from "./utils/platforms/jd/security";
import { showAssociateInvitationDialog } from "./services/pddAssociateInvitation";
import antdTheme, { antdLocale, setupAntdStaticTheme } from "@/config/antdTheme";
import AntdAppProvider from "@/components/AntdAppProvider";
import { showBrowserCompatibilityModalIfNeeded } from "@/components/BrowserCompatibilityModal";

setupAntdStaticTheme();

interface AppProps {}

const App: React.FC<AppProps> = observer(function () {
    const location = useLocation();
    const history = useHistory();

    const modalStore = useModalStore();
    const globalStore = useGlobalStore();

    const skuPropertiesFilterStore = useSkuPropertiesFilterStore();

    const currentPath = location.pathname;

    const isNeedLoadData = useMemo(() => getIsNeedLoadData(currentPath), [currentPath]);

    useEffect(() => {
        globalStore.modalStore = modalStore;
        globalStore.history = history;

        showBrowserCompatibilityModalIfNeeded(modalStore);

        const searchParams = new URLSearchParams(location.search);

        const token = searchParams.get("_token_");
        const newFeature = searchParams.get("newFeature");
        const fromIframe = searchParams.get("fromIframe");

        globalStore.fromAdmin = isFromAdmin();
        globalStore.newFeature = !!newFeature;
        globalStore.fromIframe = !!fromIframe;

        // 如果有token，保存到本地存储，并在导航栏删除token信息
        if (token) {
            AuthTokenHolder.setToken(token);

            searchParams.delete("_token_");

            const { pathname } = history.location;

            history.replace(`${pathname}?${searchParams.toString()}`);
        }

        const showUpgradePremiumModal = searchParams.get("showUpgradePremiumModal");

        // 全局注入显示高级版升级弹框函数
        window["showUpgradePremiumModal"] = (props: PremiumUpgradeModalProps) => {
            modalStore.openModal(PremiumUpgradeModal, props);
        };

        // 如果不需要加载数据，直接跳过下面的步骤
        if (!isNeedLoadData) {
            return;
        }

        // 如果没有token， 直接跳转到登录页面
        if (!AuthTokenHolder.getToken()) {
            globalStore.isInit = true;

            history.replace("/login");
            return;
        }

        // 用户真正进入后台
        // 初始化操作
        (async () => {
            await globalStore.loadCommonData();

            if (showUpgradePremiumModal) {
                let shopId = Number(searchParams.get("shopId"));
                if (!shopId) {
                    shopId = undefined;
                }

                modalStore.openModal(PremiumUpgradeModal, {
                    shopId,
                });
            }

            skuPropertiesFilterStore.loadFilterKeywords();

            const isSupplierShopUser = SupplierShopTypes.includes(globalStore.currentUserShop?.type);

            StorageManager.setLoginView(isSupplierShopUser ? "supplier" : "retailer");

            /** 抖店安全审计 */
            const hasDouDian = globalStore.allowedShops.some((shop) =>
                [
                    ShopType.DOUDIAN,
                    ShopType.DOUDIAN_IOP,
                    ShopType.DOUDIAN_SUPPLIER,
                    ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER,
                ].includes(shop.type),
            );

            if (hasDouDian) {
                const currentUserId = globalStore.currentUser.id;

                initDDJsLog();

                ByteSecurityOpenInit(currentUserId);
            }

            JdSecurityInit(globalStore);

            const currentShopId = globalStore.currentUser.shopId;
            const currentShop = globalStore.shopMapping.get(currentShopId);

            // 如果当前店铺是微信小店
            if (currentShop.type === ShopType.CHANNEL_SHOP) {
                showChannelShopApproveEwaybillAuthTips(currentShop.id);
            }

            if ([ShopType.PDD_FDS, ShopType.PDD_FULFILLMENT, ShopType.PINDUODUO].includes(currentShop.type)) {
                showAssociateInvitationDialog();
            }

            await globalStore.getShopSetting([currentShop.id]);

            const shopSetting = globalStore.shopSettingMapping.get(currentShop.id);

            if (!import.meta.env.VITE_IS_QN && import.meta.env.MODE === "production") {
                dataTrack.init(globalStore);
            }

            if (
                shopSetting &&
                isEcMarketShopExpired({
                    type: shopSetting.type,
                    subscriptionEndTime: shopSetting.subscriptionEndTime,
                }) &&
                !currentPath.includes("/subscription/order/list")
            ) {
                history.push(`/subscription/expired/${currentShop.id}`);
            }
        })();
    }, []);

    if (!globalStore.isInit && isNeedLoadData) {
        return <span>加载中...</span>;
    }

    return (
        <>
            <TopNavLayout>
                <MainRoute />
            </TopNavLayout>

            <GlobalShopExpiredModal />
        </>
    );
});

const Main: React.FC = function (props) {
    return (
        <ConfigProvider locale={antdLocale} input={{ autoComplete: "off" }} theme={antdTheme}>
            <AntdAppProvider>
                <Router>
                    <ModalStoreProvider>
                        <App />
                    </ModalStoreProvider>
                </Router>
            </AntdAppProvider>
        </ConfigProvider>
    );
};

export default React.memo(observer(Main));
